import { useSelector } from 'react-redux';
import { NavigateFunction } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { ButtonSecondary } from '@anwb/poncho/components/button';
import Form from '@anwb/poncho/components/form';
import FormFooter from '@anwb/poncho/components/form-footer';
import Typography from '@anwb/poncho/components/typography';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import {
	isFlexSelector,
	isNewCarSelector,
	leaseCarUpgradesSelector,
	paymentInAdvanceAmountSelector,
	paymentInAdvanceChoiceSelector,
	stateSelector,
} from 'store/selectors';
import { Store } from 'types';
import { GaFunnelInfo } from 'types/gaFunnelInfo';
import { STEPS, gotoStep } from 'utils/navigation';
import { scrollToError } from 'utils/scrollToError';
import { carContractValidator } from 'utils/stepValidators/carContractValidator';
import { trackEvent } from 'utils/trackEvent';

import { getContractInformationExperiment } from '../../../../utils';
import { getCarContractABTest } from '../../utils/getCarContractABTest';
import { getEquipmentModalABTest } from '../../utils/getEquipmentModalABTest';

import {
	Colors,
	ContractOverview,
	Durations,
	Kilometrage,
	Options,
	PaymentInAdvance,
	PhotoGallery,
	Specs,
	StandardEquipment,
	Upgrade,
} from './components';
import { UitrustingLink } from './components/UitrustingLink';

const onFormSubmit = (
	navigate: NavigateFunction,
	store: Store,
	gaFunnelInfo: GaFunnelInfo
): void => {
	if (carContractValidator(store)) {
		gotoStep(
			STEPS.USER_INFORMATION,
			'Verder naar persoonlijke gegevens',
			navigate,
			gaFunnelInfo,
			'navigation-button'
		);
	} else {
		scrollToError();
		trackEvent({
			event: 'user_interaction',
			type: 'form',
			component: 'button',
			field: '',
			action: 'click',
			actionValue: 'verder-naar-persoonlijke-gegevens',
			status: 'invalid:niet alle velden zijn correct ingevuld',
			...gaFunnelInfo,
		});
	}
};

export const CarContract = () => {
	const navigate = useNavigate();
	const store = useSelector(stateSelector);
	const isFlex = useSelector(isFlexSelector);
	const isNewCar = useSelector(isNewCarSelector);
	const paymentInAdvanceChoice = useSelector(paymentInAdvanceChoiceSelector);
	const paymentInAdvanceAmount = useSelector(paymentInAdvanceAmountSelector);
	const showContractInformation = getContractInformationExperiment();
	const gaFunnelInfo = useGaFunnelInfo();
	const equipmentModalABTest: string | false = getEquipmentModalABTest();
	const upgrades = useSelector(leaseCarUpgradesSelector);
	const carContractABTest = getCarContractABTest();

	return (
		<>
			<div data-test="car-information">
				<PhotoGallery />
				<Specs />
				{!isNewCar &&
					(equipmentModalABTest ? <UitrustingLink upgrade={upgrades[0]} /> : <StandardEquipment />)}
			</div>
			<Form
				id="private-lease-car-contract"
				onSubmit={() => onFormSubmit(navigate, store, gaFunnelInfo)}
				{...(!paymentInAdvanceAmount && paymentInAdvanceChoice
					? {
							errorNotification: {
								title: 'Je hebt nog geen bedrag ingevuld bij vooruitbetaling',
								variant: 'error',
								children: (
									<Typography as="span">Vul een bedrag in of zet vooruitbetaling uit.</Typography>
								),
							},
						}
					: {})}
			>
				<Upgrade />
				<Colors />
				{!isFlex && <Options />}
				{(!isNewCar || !carContractABTest) && <Durations />}
				{(!isNewCar || !carContractABTest) && <Kilometrage />}

				{!isFlex && <PaymentInAdvance />}

				{!isFlex && showContractInformation && <ContractOverview />}

				<FormFooter>
					<ButtonSecondary
						data-test="button-submit"
						className="private_lease_step_two"
						type="submit"
					>
						Verder naar persoonlijke gegevens
					</ButtonSecondary>
				</FormFooter>
			</Form>
		</>
	);
};
